<template>
  <section id="videos" class="videos">
    <h2>{{ $t('videos.title') }}</h2>
    <div class="carousel-wrapper">
      <button
        v-if="totalSlides > 1"
        type="button"
        @click="prevSlide"
        class="carousel-control left"
        :aria-label="$t('videos.previous')"
      >❮</button>
      <div class="carousel-container" ref="videoCarousel">
        <div class="carousel-track-container" @touchstart="handleTouchStart" @touchend="handleTouchEnd">
          <div class="carousel-track">
            <div v-for="video in visibleVideos" :key="video.url" class="carousel-slide">
              <template v-if="video.type === 'youtube'">
                <iframe
                  v-if="isVideoActive(video.url)"
                  :src="getYouTubeEmbedUrl(video.url)"
                  :title="$t('videos.youtubeTitle')"
                  loading="lazy"
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowfullscreen
                ></iframe>
                <button
                  v-else
                  type="button"
                  class="video-poster"
                  :aria-label="$t('videos.play')"
                  @click="activateVideo(video.url)"
                >
                  <img
                    :src="getYouTubeThumbnail(video.url)"
                    :alt="$t('videos.thumbnailAlt')"
                    loading="lazy"
                    decoding="async"
                  >
                  <span class="play-control" aria-hidden="true">
                    <span class="play-symbol"></span>
                  </span>
                </button>
              </template>
              <template v-else>
                <video
                  controls
                  preload="none"
                  playsinline
                  :src="video.url"
                  :poster="video.poster"
                  class="local-video"
                ></video>
              </template>
            </div>
          </div>
        </div>
      </div>
      <button
        v-if="totalSlides > 1"
        type="button"
        @click="nextSlide"
        class="carousel-control right"
        :aria-label="$t('videos.next')"
      >❯</button>
    </div>
  </section>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue';

const videos = [
  { url: require('@/assets/media/video/video-1.mp4'), poster: require('@/assets/media/video/video-1-poster-1200.webp'), type: 'local' },
  { url: 'https://www.youtube.com/watch?v=jWS9j9uz4X8', type: 'youtube' },
  { url: 'https://www.youtube.com/watch?v=778usP0h1_o', type: 'youtube' },
  { url: 'https://www.youtube.com/watch?v=_8VNlprEhp0', type: 'youtube' },
  // Add more video links
];

const activeVideos = ref(new Set());

const getYouTubeId = (url) => new URL(url).searchParams.get('v');

const getYouTubeEmbedUrl = (url) => {
  const videoId = getYouTubeId(url);
  return `https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&rel=0`;
};

const getYouTubeThumbnail = (url) => (
  `https://i.ytimg.com/vi/${getYouTubeId(url)}/hqdefault.jpg`
);

const isVideoActive = (url) => activeVideos.value.has(url);

const activateVideo = (url) => {
  const nextActiveVideos = new Set(activeVideos.value);
  nextActiveVideos.add(url);
  activeVideos.value = nextActiveVideos;
};

const currentSlide = ref(0);
const startX = ref(0);
const viewportWidth = ref(window.innerWidth);

const slidesPerPage = computed(() => (viewportWidth.value < 768 ? 1 : 3));
const totalSlides = computed(() => Math.ceil(videos.length / slidesPerPage.value));
const visibleStart = computed(() => currentSlide.value * slidesPerPage.value);
const visibleVideos = computed(() => (
  videos.slice(visibleStart.value, visibleStart.value + slidesPerPage.value)
));

const prevSlide = () => {
  if (currentSlide.value > 0) {
    currentSlide.value--;
  } else {
    currentSlide.value = totalSlides.value - 1;
  }
};

const nextSlide = () => {
  if (currentSlide.value < totalSlides.value - 1) {
    currentSlide.value++;
  } else {
    currentSlide.value = 0;
  }
};

const handleTouchStart = (e) => {
  startX.value = e.touches[0].clientX;
};

const handleTouchEnd = (e) => {
  const endX = e.changedTouches[0].clientX;
  if (startX.value > endX + 50) {
    nextSlide();
  } else if (startX.value < endX - 50) {
    prevSlide();
  }
};

const handleResize = () => {
  viewportWidth.value = window.innerWidth;
  currentSlide.value = 0;
};

onMounted(() => {
  window.addEventListener('resize', handleResize);
});

onUnmounted(() => {
  window.removeEventListener('resize', handleResize);
});
</script>

<style scoped>
.videos {
  padding: clamp(72px, 8vw, 104px) 20px;
  text-align: center;
  background: #fff;
}

h2 {
  margin-bottom: 40px;
}

.carousel-wrapper {
  position: relative;
  width: min(1100px, calc(100% - 48px));
  margin: 0 auto;
}

.carousel-container {
  width: 100%;
  overflow: hidden;
}

.carousel-control {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(38, 57, 77, 0.86);
  color: white;
  border: none;
  cursor: pointer;
  z-index: 10;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 24px rgba(38, 57, 77, 0.2);
  transition: background-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
  user-select: none;
  -moz-user-select: none;
  -webkit-user-drag: none;
  -webkit-user-select: none;
  -ms-user-select: none;
}

.carousel-control:hover {
  background: var(--primary-color);
  box-shadow: 0 10px 26px rgba(201, 59, 69, 0.25);
  transform: translateY(-50%) scale(1.1);
}

.carousel-control:active {
  transform: translateY(-50%) scale(1);
}

.carousel-control.left {
  left: -22px;
}

.carousel-control.right {
  right: -22px;
}

.carousel-track-container {
  overflow: hidden;
  width: 100%;
}

.carousel-track {
  display: flex;
  align-items: flex-start;
  transition: transform 0.5s ease-in-out;
}

.carousel-slide {
  flex: 0 0 33.333%;
  box-sizing: border-box;
  padding: 0 10px;
  display: flex;
  justify-content: center;
}

.carousel-slide iframe,
.carousel-slide video,
.video-poster {
  display: block;
  width: 100%;
  aspect-ratio: 16 / 9;
  height: auto;
  overflow: hidden;
  border: 0;
  border-radius: 18px;
  outline: 1px solid rgba(38, 57, 77, 0.07);
  background: #fff;
  box-shadow: 0 12px 30px rgba(38, 57, 77, 0.11);
  object-fit: cover;
}

.video-poster {
  position: relative;
  padding: 0;
  cursor: pointer;
}

.video-poster::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(0deg, rgba(20, 31, 43, 0.36), transparent 58%);
  transition: background-color 0.2s ease;
}

.video-poster > img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.play-control {
  position: absolute;
  top: 50%;
  left: 50%;
  z-index: 1;
  display: flex;
  width: 62px;
  height: 62px;
  align-items: center;
  justify-content: center;
  border: 2px solid rgba(255, 255, 255, 0.86);
  border-radius: 50%;
  background: var(--primary-color);
  box-shadow: 0 12px 28px rgba(23, 34, 46, 0.28);
  transform: translate(-50%, -50%);
  transition: background-color 0.2s ease, transform 0.2s ease;
}

.play-symbol {
  width: 0;
  height: 0;
  margin-left: 4px;
  border-top: 9px solid transparent;
  border-bottom: 9px solid transparent;
  border-left: 15px solid #fff;
}

.video-poster:hover .play-control {
  background: var(--primary-dark);
  transform: translate(-50%, -50%) scale(1.06);
}

@media (max-width: 767px) {
  .carousel-wrapper {
    width: calc(100% - 24px);
  }

  .carousel-slide {
    flex: 0 0 100%;
    padding: 0 6px;
  }

  .carousel-control {
    width: 38px;
    height: 38px;
  }

  .carousel-control.left {
    left: -12px;
  }

  .carousel-control.right {
    right: -12px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .carousel-track,
  .carousel-control {
    transition: none;
  }
}
</style>
